import { Injectable } from '@angular/core';
import { EnvService } from './env.service';
import { ActivityTypeResponse, ActivityTypes, ContentText, WireframeReportResponse } from '@mira-avant/mtlibs';
import { firstValueFrom } from 'rxjs';
import { ApiRoots, ApiService } from './api.service';

export interface ActivitySessionMessage {
	role: 'user' | 'assistant';
	text: string;
	feedback?: string;
	mp3Urls?: string[];
	npcUuid?: string;
	correct?: boolean;
}

export interface ActivitySession {
	uuid: string;
	is_promoted: boolean;
	content_uuid: string;
	take_number: number;
	messages: ActivitySessionMessage[];
}

export interface ActivitySessionMessagesResponse {
	sessions: ActivitySession[];
}

export interface CoachMessage {
	role: 'user' | 'assistant';
	text: string;
	mp3Urls?: string[];
	imageUrls?: string[];
	videoUrls?: string[];
	feedback: string;
	name: string;
	npcUuid?: string;
	correct?: boolean;
}

export interface NpcInteraction {
	messages: CoachMessage[];
	name: string;
}

export interface CoachChat {
	interactions: NpcInteraction[];
}

@Injectable({
	providedIn: 'root',
})
export class CoachService {
	private coachAudioRoot: string;

	constructor(
		private api: ApiService,
		private envService: EnvService
	) {
		this.coachAudioRoot = this.envService.getCoachAudioRoot();
	}

	private getCoachActivityUuid(contentText: ContentText): string {
		const contentUri: string | undefined = contentText.content_uri;
		if (!contentUri) {
			throw new Error('Content URI is undefined');
		}

		// contentUri looks like https://coach.metabi-poc-hub.com/?activityId=688df80c-dace-448f-906a-6cd899decea2
		const url = new URL(contentUri);
		const coachActivityUuid = url.searchParams.get('activityId');
		if (!coachActivityUuid) {
			throw new Error('Activity ID not found in content URI');
		}

		return coachActivityUuid;
	}

	/** Payload looks like
	 *
	 * {
	 *   "sessions": [
	 *     {
	 *       "uuid": "3be2b0d3-1f7a-433f-91b4-31bb716881fc",
	 *       "is_promoted": false,
	 *       "content_uuid": "4d7ee3dc-b5ef-4dcc-8eab-d62c1a096a9c",
	 *       "take_number": 1,
	 *       "messages": [
	 *         {
	 *           "role": "user",
	 *           "primaryLanguage": "That sounds fun if it's in the summer.",
	 *           "mp3Urls": [
	 *             "user/e8c1c360-9071-706f-72a5-2880f68f9b4b/3be2b0d3-1f7a-433f-91b4-31bb716881fc/d39866ed-25d6-4c0a-a011-1ffa7fb9dc77.mp3"
	 *           ]
	 *         },
	 *         {
	 *           "role": "assistant",
	 *           "primaryLanguage": "When would you like to go camping?"
	 *           "feedback": "Great! Continue to the next activity or reload the page to repeat this conversation."
	 *         },
	 *         {
	 *           "role": "user",
	 *           "primaryLanguage": "I like to camp anytime from June till September.",
	 *           "mp3Urls": [
	 *             "user/e8c1c360-9071-706f-72a5-2880f68f9b4b/3be2b0d3-1f7a-433f-91b4-31bb716881fc/0c6db686-e062-433f-bb9d-06b1fc4b5166.mp3"
	 *           ]
	 *         },
	 */
	public async getCoachChat(
		takeNumber: number,
		contentText: ContentText,
		cognitoStudentUuid: string
	): Promise<CoachChat> {
		const coachActivityUuid = this.getCoachActivityUuid(contentText);

		const apiUrl = `reporting/activityCoachUserSession/${cognitoStudentUuid}/${coachActivityUuid}/${takeNumber}`;
		const apiResult = await firstValueFrom(
			this.api.get<ActivitySessionMessagesResponse>(apiUrl, {}, ApiRoots.COACH_API)
		);

		// Convert to CoachChat format
		const interactions: NpcInteraction[] = [];

		// Handle empty sessions array
		if (!apiResult.sessions.length) {
			return { interactions };
		}

		// Find the session matching the takeNumber
		const session = apiResult.sessions.find(s => s.take_number === takeNumber);
		if (!session) {
			return { interactions };
		}

		// Create a single interaction from the session messages
		const messages: CoachMessage[] = [];
		for (const entry of session.messages) {
			if (entry.role === 'user') {
				messages.push({
					role: 'user',
					text: entry.text,
					feedback: entry.feedback || '',
					correct: entry.correct,
					name: 'Student',
					mp3Urls: entry.mp3Urls ? entry.mp3Urls.map(url => `${this.coachAudioRoot}/${url}`) : undefined,
				});
			} else if (entry.role === 'assistant') {
				messages.push({
					role: 'assistant',
					text: entry.text,
					feedback: entry.feedback || '',
					name: 'Assistant', //variety here causes different colors on the frontend - one color per name.
					npcUuid: entry.npcUuid,
					mp3Urls: undefined, // Assistant messages don't have mp3Urls
					correct: entry.correct,
				});
			}
		}

		if (messages.length > 0) {
			interactions.push({
				messages,
				name: '...', //this used to mean something, doesn't anymore
			});
		}

		return { interactions };
	}

	public async getActivityType(contentText: ContentText): Promise<ActivityTypes | undefined> {
		const coachActivityUuid = this.getCoachActivityUuid(contentText);
		const apiUrl = `reporting/activityDetails/${coachActivityUuid}`;
		const apiResult = await firstValueFrom(this.api.get<ActivityTypeResponse>(apiUrl, {}, ApiRoots.COACH_API));

		return apiResult.type;
	}

	public async getWireframeReport(
		takeNumber: number,
		contentText: ContentText,
		cognitoStudentUuid: string
	): Promise<WireframeReportResponse> {
		const coachActivityUuid = this.getCoachActivityUuid(contentText);
		const apiUrl = `reporting/activityWireframeUserSession/${cognitoStudentUuid}/${coachActivityUuid}/${takeNumber}`;

		return firstValueFrom(this.api.get<WireframeReportResponse>(apiUrl, {}, ApiRoots.COACH_API));
	}

	/**
	 * Payload looks like:
	 *
	 * {
	 *   "sessions": [
	 *     {
	 *       "uuid": "c7d64599-586e-4111-a186-b3f809d71100",
	 *       "hubs_room_sid": null,
	 *       "data_json": {
	 *         "task": 0,
	 *         "npcActivities": {
	 *           "ea069359-7497-4b19-8b12-8dd3732ea2d6": {
	 *             "history": [
	 *               {
	 *                 "role": "assistant",
	 *                 "content": "{\"R\":\"_R1\"}"
	 *               }
	 *             ]
	 *           }
	 *         }
	 *       },
	 *       "is_promoted": true,
	 *       "created_ts": "2025-02-10T18:38:04.386Z",
	 *       "take_number": 1
	 *     }
	 *   ]
	 * }
	 *
	 */
	// public async _old_getSessions(
	// 	contentText: ContentText,
	// 	cognitoStudentUuid: string
	// ): Promise<SessionsResponse> {
	// 	const contentUri: string | undefined = contentText.content_uri;
	// 	if (!contentUri) {
	// 		throw new Error('Content URI is undefined');
	// 	}
	// 	//contentUri looks like https://coach.metabi-poc-hub.com/?activityId=688df80c-dace-448f-906a-6cd899decea2
	// 	const url = new URL(contentUri);
	// 	const coachActivityUuid = url.searchParams.get('activityId');
	// 	if (!coachActivityUuid) {
	// 		throw new Error('Activity ID not found in content URI');
	// 	}
	// 	const apiUrl = `${this.coachApiRoot}reporting/SessionsByActivity/${cognitoStudentUuid}/${coachActivityUuid}`;
	// 	return firstValueFrom(this.http.get<SessionsResponse>(apiUrl));
	// }

	/**
	 * Format
	 *
	 * {
	 *   "llm_actions": [
	 *     {
	 *       "uuid": "f8579253-612b-4dad-9646-9270c8cad48f",
	 *       "session_uuid": "00f76efd-bc3d-499d-b568-d584d8c8a903",
	 *       "context": null,
	 *       "data_json": {
	 *         "npc_uuid": "a42d0327-cc65-40f2-ae50-c768d88c7ff5",
	 *         "task": 0
	 *       },
	 *       "created_ts": "2025-09-23T16:32:13.079Z",
	 *       "updated_ts": "2025-09-23T16:32:13.079Z",
	 *       "type": "SPEECH_LLM_TRANSACTION",
	 *       "user_uuid": "2841d3c0-30f1-70b7-9b2a-51769d6df3e7",
	 *       "interaction_type": null,
	 *       "units": [
	 *         {
	 *           "uuid": "3d3f7aa7-41f9-4d2c-a27d-e0a7550053ce",
	 *           "session_uuid": "00f76efd-bc3d-499d-b568-d584d8c8a903",
	 *           "session_action_uuid": "f8579253-612b-4dad-9646-9270c8cad48f",
	 *           "user_uuid": "2841d3c0-30f1-70b7-9b2a-51769d6df3e7",
	 *           "type": "WAV",
	 *           "duration": 142,
	 *           "text": null,
	 *           "data_json": null,
	 *           "created_ts": "2025-09-23T16:32:13.342Z"
	 *         },
	 *         {
	 *           "uuid": "34dd66cf-c999-42e3-8595-598d96821b85",
	 *           "session_uuid": "00f76efd-bc3d-499d-b568-d584d8c8a903",
	 *           "session_action_uuid": "f8579253-612b-4dad-9646-9270c8cad48f",
	 *           "user_uuid": "2841d3c0-30f1-70b7-9b2a-51769d6df3e7",
	 *           "type": "STT",
	 *           "duration": 2197,
	 *           "text": null,
	 *           "data_json": {
	 *             "sound_url": "user/2841d3c0-30f1-70b7-9b2a-51769d6df3e7/00f76efd-bc3d-499d-b568-d584d8c8a903/f8579253-612b-4dad-9646-9270c8cad48f.mp3"
	 *           },
	 *           "created_ts": "2025-09-23T16:32:15.544Z"
	 *         }
	 *       ]
	 *     },
	 *   ],
	 *   "form_actions": [ ]
	 * }
	 *
	 * @public
	 * @async
	 * @param {ContentText} contentText
	 * @param {string} cognitoStudentUuid
	 * @returns {unknown}
	 */
	// public async _old_getActivitySessionActions(
	// 	contentText: ContentText,
	// 	cognitoStudentUuid: string
	// ) {
	// 	const contentUri: string | undefined = contentText.content_uri;
	// 	if (!contentUri) {
	// 		throw new Error('Content URI is undefined');
	// 	}
	// 	//contentUri looks like https://coach.metabi-poc-hub.com/?activityId=688df80c-dace-448f-906a-6cd899decea2
	// 	const url = new URL(contentUri);
	// 	const coachActivityUuid = url.searchParams.get('activityId');
	// 	if (!coachActivityUuid) {
	// 		throw new Error('Activity ID not found in content URI');
	// 	}
	// 	const apiUrl = `${this.coachApiRoot}reporting/ActivitySessionActions/${cognitoStudentUuid}/${coachActivityUuid}`;
	// 	return firstValueFrom(this.http.get<ActivitySessionActionsResponse>(apiUrl));
	// }

	// public async __old_getCoachChatTest(
	// 	contentText: ContentText,
	// 	cognitoStudentUuid: string
	// ): Promise<CoachChat> {
	// 	const npcInteractions: NpcInteraction[] = [];
	// 	const messages: CoachMessage[] = [
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				"Hello! I'm your Spanish language coach. What would you like to practice today?",
	// 			feedback: '¡Hola! Soy tu profesor de español. ¿Qué te gustaría practicar hoy?',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'I want to practice ordering food at a restaurant',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Great choice! Let\'s start with basic restaurant vocabulary. Do you know how to say "menu" in Spanish?',
	// 			feedback:
	// 				'¡Excelente elección! Comencemos con el vocabulario básico de restaurante. ¿Sabes cómo se dice "menu" en español?',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Is it "el menú"?',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Correct! Now, let\'s practice a common phrase: "I would like to order..."',
	// 			feedback:
	// 				'¡Correcto! Ahora, practiquemos una frase común: "Me gustaría ordenar..."',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Me gustaría ordenar...',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Excellent pronunciation! Let\'s add to that. "I would like to order a coffee."',
	// 			feedback:
	// 				'¡Excelente pronunciación! Agreguemos más. "Me gustaría ordenar un café."',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Me gustaría ordenar un café',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Perfect! Now let\'s learn how to ask for the check: "The check, please."',
	// 			feedback: '¡Perfecto! Ahora aprendamos a pedir la cuenta: "La cuenta, por favor."',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'La cuenta, por favor',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage: 'Would you like to practice ordering a full meal?',
	// 			feedback: '¿Te gustaría practicar cómo ordenar una comida completa?',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Yes, please!',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Let\'s start with: "I would like the soup of the day as a starter."',
	// 			feedback: 'Me gustaría la sopa del día como entrada.',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Me gustaría la sopa del día como entrada',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Great! For the main course: "For the main dish, I would like the grilled chicken."',
	// 			feedback: 'Para el plato principal, me gustaría el pollo a la parrilla.',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Para el plato principal, me gustaría el pollo a la parrilla',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Excellent! Now for dessert: "And for dessert, I would like the chocolate cake."',
	// 			feedback: 'Y de postre, me gustaría el pastel de chocolate.',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Y de postre, me gustaría el pastel de chocolate',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Perfect! Let\'s practice asking about ingredients: "What ingredients does this dish have?"',
	// 			feedback: '¿Qué ingredientes tiene este plato?',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: '¿Qué ingredientes tiene este plato?',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage: 'Now let\'s learn to say: "I have a food allergy."',
	// 			feedback: 'Tengo una alergia alimentaria.',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'Tengo una alergia alimentaria',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage: 'Very good! Let\'s practice saying: "Is this dish spicy?"',
	// 			feedback: '¿Este plato es picante?',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: '¿Este plato es picante?',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				'Excellent progress! One last useful phrase: "Everything was delicious!"',
	// 			feedback: '123 estuvo delicioso!',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: '123 estuvo delicioso!',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				"Great job with today's restaurant vocabulary! Would you like to practice these phrases again?",
	// 			feedback:
	// 				'¡Buen trabajo con el vocabulario de restaurante de hoy! ¿Te gustaría practicar estas frases nuevamente?',
	// 			name: 'Assistant',
	// 		},
	// 		{
	// 			role: 'user',
	// 			primaryLanguage: 'No thank you, I feel confident now!',
	// 			feedback: '',
	// 			name: 'User',
	// 		},
	// 		{
	// 			role: 'assistant',
	// 			primaryLanguage:
	// 				"Wonderful! You've made excellent progress. Remember to practice these phrases before your next restaurant visit!",
	// 			feedback:
	// 				'¡Maravilloso! Has progresado excelentemente. ¡Recuerda practicar estas frases antes de tu próxima visita al restaurante!',
	// 			name: 'Assistant',
	// 		},
	// 	];
	// 	npcInteractions.push({ messages, name: 'Assistant' });
	// 	return { interactions: npcInteractions };
	// }

	// public async _old_getCoachChat(
	// 	takeNumber: number,
	// 	contentText: ContentText,
	// 	cognitoStudentUuid: string
	// ): Promise<CoachChat> {
	// 	// First get the sessions and actions responses
	// 	const [sessionsResponse, actionsResponse] = await Promise.all([
	// 		this._old_getSessions(contentText, cognitoStudentUuid),
	// 		this._old_getActivitySessionActions(contentText, cognitoStudentUuid),
	// 	]);

	// 	// Convert to CoachChat format
	// 	const interactions: NpcInteraction[] = [];

	// 	// Handle empty sessions array
	// 	if (!sessionsResponse.sessions.length) {
	// 		return { interactions };
	// 	}

	// 	// Get the first session and its NPC activity
	// 	const firstSession = sessionsResponse.sessions.find(
	// 		(session: Session) => session.take_number === takeNumber
	// 	);
	// 	if (!firstSession) {
	// 		return { interactions };
	// 	}

	// 	// Build map of audio URLs by session and user UUID
	// 	const audioMap: { [sessionUuid: string]: { [userUuid: string]: string[] } } = {};
	// 	actionsResponse.llm_actions.forEach(action => {
	// 		if (action.session_uuid === firstSession.uuid) {
	// 			if (!audioMap[action.session_uuid]) {
	// 				audioMap[action.session_uuid] = {};
	// 			}
	// 			if (!audioMap[action.session_uuid][action.user_uuid]) {
	// 				audioMap[action.session_uuid][action.user_uuid] = [];
	// 			}
	// 			const mp3Urls = action.units
	// 				.filter(
	// 					unit => unit.type === 'STT' && unit.data_json?.sound_url?.endsWith('.mp3')
	// 				)
	// 				.map(unit => `${this.coachAudioRoot}/${unit.data_json!.sound_url}`);
	// 			audioMap[action.session_uuid][action.user_uuid].push(...mp3Urls);
	// 		}
	// 	});

	// 	const npcInteractionUuids: string[] = Object.keys(firstSession.data_json.npcActivities);
	// 	for (const npcInteractionUuid of npcInteractionUuids) {
	// 		const npcActivity = firstSession.data_json.npcActivities[npcInteractionUuid];
	// 		const messages: CoachMessage[] = [];
	// 		let audioIndex = 0;

	// 		// Process each message in the history
	// 		for (const entry of npcActivity.history) {
	// 			// Skip control messages (messages with just R field)
	// 			try {
	// 				// Handle regular user messages
	// 				if (entry.role === 'user') {
	// 					const availableAudioUrls =
	// 						audioMap[firstSession.uuid]?.[cognitoStudentUuid] || [];
	// 					const mp3Urls =
	// 						availableAudioUrls.length > audioIndex
	// 							? [availableAudioUrls[audioIndex++]]
	// 							: undefined;
	// 					messages.push({
	// 						role: 'user',
	// 						primaryLanguage: entry.content,
	// 						feedback: '', // User messages don't have translations
	// 						name: 'Student',
	// 						mp3Urls,
	// 					});
	// 					continue;
	// 				}

	// 				// Handle assistant messages with translations
	// 				if (entry.role === 'assistant') {
	// 					const content = JSON.parse(entry.content);
	// 					// Skip messages that only have an R field (control messages)
	// 					if (Object.keys(content).length === 1 && content.R) {
	// 						continue;
	// 					}
	// 					if (!content.V && !content.F) {
	// 						continue;
	// 					}
	// 					messages.push({
	// 						role: 'assistant',
	// 						primaryLanguage: content.V,
	// 						feedback: content.F,
	// 						name: npcActivity.name || 'Assistant',
	// 					});
	// 				}
	// 			} catch (e) {
	// 				console.error('Error parsing JSON:', e);
	// 				// If JSON.parse fails, treat it as a regular message
	// 				if (entry.role === 'user') {
	// 					messages.push({
	// 						role: 'user',
	// 						primaryLanguage: entry.content,
	// 						feedback: '',
	// 						name: 'Student',
	// 					});
	// 				}
	// 			}
	// 		}
	// 		interactions.push({ messages, name: npcActivity.name });
	// 	}
	// 	return { interactions };
	// }
}
